import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Outlet, useNavigate } from "react-router-dom";
import ListEntitiesHeaderComponent from "../../../components/ListEntities/ListEntitiesHeaderComponent";
import ListEntitiesTable from "../../../components/ListEntities/ListEntitiesTable";
import { Aircraft } from "../../../models/aircraft";
import { getAircraft } from "../../../store/aircraftSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import LoadingComponent from "../../../components/LoadingComponent";

interface ListAicraftPageProps {
  onAircraftSelected?: (aircraft: Aircraft) => void;
  //picking for an IFR flight: IFR-certified aircraft sort on top and get an IFR badge
  ifrPick?: boolean;
}

function ListAicraftPage({ onAircraftSelected, ifrPick }: ListAicraftPageProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { allAircraft, status, loading } = useAppSelector((state) => state.aircraft);

  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (status === "idle") {
      dispatch(getAircraft());
    }
  }, [dispatch, status]);

  return (
    <Container fluid className="vh-100 px-0 px-md-2">
      <ListEntitiesHeaderComponent
        search={{ value: searchValue, setValue: setSearchValue }}
        title="Aircraft"
        actionBtn={{ title: "Add Aircraft", onClick: () => navigate("/aircraft/add") }}
      />

      {loading ? (
        <LoadingComponent text="Loading aircraft..." />
      ) : (
        <Row className="overflow-auto" style={{ height: "calc(100% - 140px)" }}>
          <Col>
            <ListAircraftTable data={allAircraft} searchValue={searchValue} onAircraftSelected={onAircraftSelected} ifrPick={ifrPick} />
          </Col>
        </Row>
      )}

      <Outlet />
    </Container>
  );
}

export default ListAicraftPage;

interface ListAircraftTableProps {
  data: Aircraft[];
  searchValue: string;
  onAircraftSelected?: (aircraft: Aircraft) => void;
  ifrPick?: boolean;
}

function ListAircraftTable({ data, searchValue, onAircraftSelected, ifrPick }: ListAircraftTableProps) {
  const columnHelper = createColumnHelper<Aircraft>();

  //Always deterministic, never the raw fetch order: the same row position must offer the same aircraft
  //on every opening. Unsorted, picking by position silently validates a route against a different
  //aircraft than the one intended — which is exactly how a correct IFPS rejection was once read as an
  //intermittent bug. Sorted by Name because that is the column being scanned, registration as the
  //tie-break for the org's informal names. IFR-capable float to the top only when picking for IFR.
  const rows = [...data].sort((a, b) =>
    (ifrPick ? Number(b.ifrCertified === true) - Number(a.ifrCertified === true) : 0) ||
    (a.name ?? "").localeCompare(b.name ?? "") ||
    (a.registration ?? "").localeCompare(b.registration ?? "")
  );

  const columns = [
    columnHelper.accessor("name", {
      header: () => "Name",
      sortingFn: "alphanumeric",
      //IFR-certified aircraft carry an IFR badge everywhere the list shows — the plain aircraft
      //list and the IFR flight picker alike (the picker additionally sorts them to the top).
      cell: (info) =>
        info.row.original.ifrCertified ? (
          <span className="d-inline-flex align-items-center gap-2">
            {info.getValue()}
            <span className="badge bg-danger" title="IFR certified">IFR</span>
          </span>
        ) : (
          info.getValue()
        ),
    }),
    columnHelper.accessor("registration", {
      header: () => "Reg",
      sortingFn:"alphanumeric"
    }),
    columnHelper.accessor("operatorDefaultNumber", {
      header: () => "Designator",
      sortingFn:"alphanumeric"
    }),
    columnHelper.accessor("icaoType", {
      header: () => "Type",
      sortingFn:"alphanumeric"
    }),

  ];

  return (
    <>
      <ListEntitiesTable
        data={rows}
        columns={columns}
        searchValue={searchValue}
        onRowClick={onAircraftSelected}
      />
    </>
  );
}
  